import React, { useState, useCallback } from 'react';
import GenericConfirmationModal, {
  GenericConfirmationModalProps,
} from '../components/Modals/GenericConfirmationModal';

type ConfirmationConfig = Omit<
  GenericConfirmationModalProps,
  'onConfirm' | 'onCancel'
>;

export const useConfirmationModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ConfirmationConfig | null>(null);
  const [onConfirmCallback, setOnConfirmCallback] = useState<
    (() => void) | null
  >(null);

  const openModal = useCallback(
    (modalConfig: ConfirmationConfig, onConfirm: () => void) => {
      setConfig(modalConfig);
      setOnConfirmCallback(() => onConfirm);
      setIsOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setConfig(null);
    setOnConfirmCallback(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (onConfirmCallback) {
      onConfirmCallback();
    }
    closeModal();
  }, [onConfirmCallback, closeModal]);

  const ConfirmationModal = useCallback(() => {
    if (!isOpen || !config) return null;

    return (
      <GenericConfirmationModal
        {...config}
        onConfirm={handleConfirm}
        onCancel={closeModal}
      />
    );
  }, [isOpen, config, handleConfirm, closeModal]);

  return {
    openModal,
    closeModal,
    ConfirmationModal,
    isOpen,
  };
};
